const unidecode = require('unidecode');

const COUNTRY_SELECTOR_MODAL_ID = 'country-selector-modal';
const {
  deleteCookie,
  names: { IGNORE_GEOLOCATION_COOKIE_NAME },
} = require('../../common/utils/cookieUtils');

/**
 * Show the country selector modal
 */
function show() {
  const countrySelectorModalElement = document.getElementById(
    COUNTRY_SELECTOR_MODAL_ID
  );
  countrySelectorModalElement?.showModal();
}

/**
 * Show the search view for the modal
 */
function enableSearchView() {
  const allCountriesGroup = document.querySelector(
    '.country-selector-modal__all-countries-group'
  );
  if (!allCountriesGroup) {
    return;
  }

  document
    .querySelectorAll(
      '.country-selector-modal__group:not(.country-selector-modal__all-countries-group)'
    )
    .forEach((element) => {
      element.classList.add('d-none');
    });
  const allCountriesGroupTitle = document.querySelector(
    '.country-selector-modal__all-countries-group .country-selector-modal__group-title'
  );
  allCountriesGroupTitle?.classList.add('d-none');
  allCountriesGroup.classList.add(
    'country-selector-modal__all-countries-group--searching'
  );
  allCountriesGroup.open = true;
}

/**
 * Show the default view for the modal
 */
function disableSearchView() {
  document
    .querySelectorAll(
      '.country-selector-modal__group:not(.country-selector-modal__all-countries-group)'
    )
    .forEach((element) => {
      element.classList.remove('d-none');
    });
  const allCountriesGroupTitle = document.querySelector(
    '.country-selector-modal__all-countries-group .country-selector-modal__group-title'
  );
  allCountriesGroupTitle?.classList.remove('d-none');

  const allCountriesGroup = document.querySelector(
    '.country-selector-modal__all-countries-group'
  );
  if (allCountriesGroup) {
    allCountriesGroup.classList.remove(
      'country-selector-modal__all-countries-group--searching'
    );
    allCountriesGroup.open = false;
  }
}

/**
 * Build a regex from the query, normalizing it.
 * @param {string} query - The search query.
 * @returns {RegExp|null} - The search query regex.
 */
function buildSearchRegex(query) {
  const normalizedQuery = unidecode(
    query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape special characters
  );
  return normalizedQuery !== '' ? new RegExp(normalizedQuery, 'gi') : null;
}

/**
 * Highlight the regex match within the original label.
 * @param {string} original - The full country name with accents.
 * @param {string} normalized - The full normalized country name.
 * @param {RegExp} regex - The search regex.
 * @returns {string} - Inner HTML with matched substring highlighted.
 */
function highlightMatch(original, normalized, regex) {
  // eslint-disable-next-line no-param-reassign
  regex.lastIndex = 0;
  const match = regex.exec(normalized);
  if (!match) return original;

  const start = match.index;
  const end = start + match[0].length;

  const before = original.slice(0, start);
  const matched = original.slice(start, end);
  const after = original.slice(end);

  return `${before}<span class="country-selector-modal__search-match">${matched}</span>${after}`;
}

/**
 * Show or hide search view depending on active search.
 * @param {boolean} isSearching - True if searching, false otherwise
 */
function toggleSearchView(isSearching) {
  if (isSearching) {
    enableSearchView();
  } else {
    disableSearchView();
  }
}

/**
 * Handle the country search input and highlight matches.
 * @param {Event} event - The input event from the search field.
 */
function handleCountrySearch(event) {
  const searchedValue = event.target.value;
  const allCountryItems = document.querySelectorAll(
    '.country-selector-modal__all-countries-group .country-selector-modal__country-item'
  );

  const searchRegex = buildSearchRegex(searchedValue);

  allCountryItems.forEach((element) => {
    if (!searchRegex) {
      element.classList.remove('d-none');
      return;
    }

    const inputElement = element.querySelector('input');
    const originalName = inputElement.name;
    const normalizedName = unidecode(originalName);

    searchRegex.lastIndex = 0;
    if (searchRegex.test(normalizedName)) {
      const labelElement = element.querySelector('label');
      labelElement.innerHTML = highlightMatch(
        originalName,
        normalizedName,
        searchRegex
      );

      element.classList.remove('d-none');
    } else {
      element.classList.add('d-none');
    }
  });

  toggleSearchView(searchedValue !== '');
}

/**
 * Hides Close button by adding clas dialog-modal--no-close
 */
function hideCloseBtn() {
  const countrySelectorModalElement = document.getElementById(
    COUNTRY_SELECTOR_MODAL_ID
  );
  countrySelectorModalElement?.classList.add('dialog-modal--no-close');
}

/**
 * Listen for the country search
 */
function listenForCountrySearch() {
  const {
    addDocumentDelegatedListener,
  } = require('../../common/utils/domEvents');
  addDocumentDelegatedListener(
    'input',
    '.js-country-selector-modal-search',
    handleCountrySearch
  );
}

/**
 * Update the language selector with the languages passed as parameter
 * @param {Object.<string, Language>} languages Languages information
 */
function updatelLanguagesSelector(languages) {
  const languageSectionElement = document.querySelector(
    '.country-selector-modal__language-section'
  );
  if (!languageSectionElement) {
    return;
  }

  document
    .querySelectorAll(
      '.country-selector-modal__language-item:not(.country-selector-modal__language-item--template)'
    )
    .forEach((item) => item.remove());

  const languageItemTemplate = document.querySelector(
    '.country-selector-modal__language-item--template'
  );
  Object.values(languages).forEach((language) => {
    const languageItem = languageItemTemplate.cloneNode(true);
    const languageInputElement = languageItem.querySelector('input');
    const languageLabelElement = languageItem.querySelector('label');
    if (!languageInputElement || !languageLabelElement) {
      return;
    }

    languageInputElement.setAttribute('id', language.language);
    languageInputElement.setAttribute('value', language.language);
    languageLabelElement.setAttribute('for', language.language);
    languageInputElement.setAttribute(
      'name',
      language.displayLanguage.toUpperCase()
    );
    languageLabelElement.innerText = language.displayLanguage.toUpperCase();
    languageLabelElement.dataset.setCookieUrl = language.urlSetCookie;

    languageItem.classList.remove(
      'country-selector-modal__language-item--template'
    );
    languageItem.classList.remove('d-none');

    document
      .querySelector('.country-selector-modal__languages-list')
      ?.appendChild(languageItem);
  });
}

/**
 * Listen for the country selection
 */
function listenCountrySelection() {
  const {
    addDocumentDelegatedListener,
  } = require('../../common/utils/domEvents');
  addDocumentDelegatedListener(
    'click',
    '.js-country-selector-country-item',
    (event) => {
      const countryItemElement = event.target.closest(
        '.js-country-selector-country-item'
      );
      const languages = countryItemElement.dataset.languages
        ? JSON.parse(countryItemElement.dataset.languages)
        : {};

      if (Object.keys(languages).length <= 1) {
        deleteCookie(IGNORE_GEOLOCATION_COOKIE_NAME);

        const setCookieURL = countryItemElement.dataset.setCookieUrl;
        window.location.href = setCookieURL;
      } else {
        updatelLanguagesSelector(languages);

        const countrySectionElement = document.querySelector(
          '.country-selector-modal__country-section'
        );
        countrySectionElement?.classList.add('d-none');

        const languageSectionElement = document.querySelector(
          '.country-selector-modal__language-section'
        );
        languageSectionElement?.classList.remove('d-none');
      }
    }
  );
}

/**
 * Listen for language section back button click
 */
function listenLanguageSectionBackButton() {
  const {
    addDocumentDelegatedListener,
  } = require('../../common/utils/domEvents');
  addDocumentDelegatedListener(
    'click',
    '.country-selector-modal .btn--back-black',
    () => {
      const languageSectionElement = document.querySelector(
        '.country-selector-modal__language-section'
      );
      languageSectionElement?.classList.add('d-none');

      const countrySectionElement = document.querySelector(
        '.country-selector-modal__country-section'
      );
      countrySectionElement?.classList.remove('d-none');
    }
  );
}

/**
 * Listen for the language selection
 */
function listenLanguageSelection() {
  const {
    addDocumentDelegatedListener,
  } = require('../../common/utils/domEvents');
  addDocumentDelegatedListener(
    'click',
    '.js-country-selector-language-item',
    (event) => {
      deleteCookie(IGNORE_GEOLOCATION_COOKIE_NAME);

      const setCookieURL = event.target.dataset.setCookieUrl;
      window.location.href = setCookieURL;
    }
  );
}

module.exports = {
  methods: {
    show,
    hideCloseBtn,
  },
  listenCountrySelection,
  listenForCountrySearch,
  listenLanguageSectionBackButton,
  listenLanguageSelection,
};
